import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  AlertTriangle,
  X,
  Clock,
  Globe2,
  Plus,
  ShoppingCart,
  Search,
  CalendarClock,
  Ban,
  ArrowUpRight,
} from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { Card, Badge, EmptyState, cx } from '../components/ui';

// Domains Library (PRD: Library → Domains). Pavlov does the deliverability reasoning; the admin makes
// the human decision. Status is conveyed monochrome (fill + shape) to match the app theme.
type Check = 'pass' | 'warn' | 'fail';
interface Domain {
  id: string;
  domain: string;
  tld: string;
  source: string;
  style: string | null;
  theme: string | null;
  is_pool: boolean;
  readiness: {
    state: 'ready' | 'warming' | 'attention' | 'not_ready';
    checks: Record<string, Check>;
  };
  condition: {
    state: 'healthy' | 'cooling' | 'watch' | 'burned';
    reputation: Check;
    burned: boolean;
  };
  last_used_at: string | null;
  days_since_use: number | null;
  usage_history: { client: string; campaign: string; date: string }[];
  registration_expiry: string | null;
  ssl_expiry: string | null;
  notes: string | null;
}

const READINESS_LABEL = {
  ready: 'Ready',
  warming: 'Warming',
  attention: 'Needs attention',
  not_ready: 'Not ready',
} as const;
const CONDITION_LABEL = {
  healthy: 'Healthy',
  cooling: 'Cooling down',
  watch: 'Watch',
  burned: 'Burned',
} as const;
const SOURCE_LABEL: Record<string, string> = {
  hook_shared: 'Shared Pool', // white-label display; the source value stays `hook_shared` (decision #5)
  imported: 'Imported',
  cloudflare: 'Cloudflare',
};

function CheckGlyph({ v }: { v: Check }) {
  if (v === 'pass') return <Check className="h-3 w-3 text-ink" />;
  if (v === 'warn') return <AlertTriangle className="h-3 w-3 text-muted" />;
  return <X className="h-3 w-3 text-ink" />;
}

function MiniChecks({ checks }: { checks: Record<string, Check> }) {
  const items: [string, string][] = [
    ['DNS', 'dns'],
    ['SSL', 'ssl'],
    ['Deliv', 'deliverability'],
    ['Warm', 'warming'],
  ];
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {items.map(([label, key]) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 text-[11px] text-muted"
          title={`${label}: ${checks[key]}`}
        >
          {label}
          <CheckGlyph v={checks[key] ?? 'warn'} />
        </span>
      ))}
    </div>
  );
}

function ReadinessBadge({ state }: { state: Domain['readiness']['state'] }) {
  const Icon =
    state === 'ready'
      ? Check
      : state === 'warming'
        ? Clock
        : state === 'not_ready'
          ? X
          : AlertTriangle;
  return (
    <Badge tone={state === 'ready' ? 'solid' : state === 'not_ready' ? 'muted' : 'outline'}>
      <Icon className="h-3 w-3" /> {READINESS_LABEL[state]}
    </Badge>
  );
}
function ConditionBadge({ state }: { state: Domain['condition']['state'] }) {
  const Icon =
    state === 'healthy'
      ? Check
      : state === 'cooling'
        ? Clock
        : state === 'burned'
          ? Ban
          : AlertTriangle;
  return (
    <Badge tone={state === 'healthy' ? 'outline' : state === 'burned' ? 'solid' : 'muted'}>
      <Icon className="h-3 w-3" /> {CONDITION_LABEL[state]}
    </Badge>
  );
}

function daysUntil(date: string | null): number | null {
  if (!date) return null;
  return Math.ceil((Date.parse(date) - Date.now()) / 86_400_000);
}

function Drawer({ d, onClose }: { d: Domain; onClose: () => void }) {
  const navigate = useNavigate();
  const regDays = daysUntil(d.registration_expiry);
  const sslDays = daysUntil(d.ssl_expiry);
  const assignable = d.readiness.state !== 'not_ready' && d.condition.state !== 'burned';
  return (
    <div className="fixed inset-0 z-30 flex justify-end">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-auto border-l border-line bg-paper">
        <div className="flex items-start justify-between border-b border-line px-6 py-5">
          <div>
            <div className="font-display text-xl text-ink">{d.domain}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
              <Badge tone="muted">{SOURCE_LABEL[d.source] ?? d.source}</Badge>
              {d.style && (
                <span>{d.style === 'brand_lookalike' ? 'Brand lookalike' : 'Generic'}</span>
              )}
              {d.theme && <span>· {d.theme}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" title="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="flex flex-wrap gap-2">
            <ReadinessBadge state={d.readiness.state} />
            <ConditionBadge state={d.condition.state} />
          </div>

          <div>
            <div className="eyebrow mb-2">Readiness checks</div>
            <Card className="divide-y divide-line">
              {Object.entries(d.readiness.checks).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="capitalize text-muted">{k}</span>
                  <span className="inline-flex items-center gap-1.5 text-ink">
                    <CheckGlyph v={v} /> {v}
                  </span>
                </div>
              ))}
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-input border border-line px-3 py-2">
              <div className="text-xs uppercase tracking-wide text-muted">Reg. expiry</div>
              <div className={cx('text-ink', regDays !== null && regDays < 60 && 'font-medium')}>
                {d.registration_expiry ?? '—'} {regDays !== null && `(${regDays}d)`}
              </div>
            </div>
            <div className="rounded-input border border-line px-3 py-2">
              <div className="text-xs uppercase tracking-wide text-muted">SSL expiry</div>
              <div className={cx('text-ink', sslDays !== null && sslDays < 30 && 'font-medium')}>
                {d.ssl_expiry ?? '—'} {sslDays !== null && `(${sslDays}d)`}
              </div>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2">Client usage history</div>
            {d.usage_history.length === 0 ? (
              <p className="text-sm text-muted">Never used — safe for any client.</p>
            ) : (
              <Card className="divide-y divide-line">
                {d.usage_history.map((u, i) => (
                  <div key={i} className="px-4 py-2.5 text-sm">
                    <div className="text-ink">{u.client}</div>
                    <div className="text-xs text-muted">
                      {u.campaign} · {u.date}
                    </div>
                  </div>
                ))}
              </Card>
            )}
          </div>

          {d.notes && (
            <div>
              <div className="eyebrow mb-2">Notes</div>
              <p className="text-sm text-muted">{d.notes}</p>
            </div>
          )}
        </div>

        <div className="mt-auto flex gap-2 border-t border-line px-6 py-4">
          <button
            disabled={!assignable}
            onClick={() => navigate('/campaigns?tab=config')}
            className="btn-primary flex-1"
            title={assignable ? '' : 'Not assignable until Ready and not Burned'}
          >
            Assign to campaign
          </button>
          <button className="btn-outline" title="Report a deliverability/reputation issue">
            Report issue
          </button>
        </div>
      </div>
    </div>
  );
}

export function DomainsLibrary() {
  const { selectedOrg } = useOrg();
  const { data, error } = useLoad(() => api.get<{ data: Domain[] }>('/v1/domains'));
  const all = useMemo(() => data?.data ?? [], [data]);

  const [readiness, setReadiness] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [style, setStyle] = useState<string>('');
  const [theme, setTheme] = useState<string>('');
  const [search, setSearch] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [selected, setSelected] = useState<Domain | null>(null);

  const themes = useMemo(
    () => Array.from(new Set(all.map((d) => d.theme).filter(Boolean))) as string[],
    [all],
  );

  const filtered = all.filter((d) => {
    if (readiness && d.readiness.state !== readiness) return false;
    if (source && d.source !== source) return false;
    if (style && d.style !== style) return false;
    if (theme && d.theme !== theme) return false;
    if (search && !`${d.domain} ${d.theme ?? ''}`.toLowerCase().includes(search.toLowerCase()))
      return false;
    if (availableOnly && selectedOrg) {
      if (d.readiness.state === 'not_ready' || d.condition.state === 'burned') return false;
      if (d.usage_history.some((u) => u.client === selectedOrg.name)) return false;
    }
    return true;
  });

  const counts = {
    total: all.length,
    ready: all.filter((d) => d.readiness.state === 'ready').length,
    warming: all.filter((d) => d.readiness.state === 'warming').length,
    unavailable: all.filter(
      (d) => d.readiness.state === 'not_ready' || d.condition.state === 'burned',
    ).length,
    expiring: all.filter((d) => {
      const r = daysUntil(d.registration_expiry);
      const s = daysUntil(d.ssl_expiry);
      return (r !== null && r < 60) || (s !== null && s < 30);
    }).length,
  };

  const Pill = ({
    active,
    onClick,
    children,
  }: {
    active: boolean;
    onClick: () => void;
    children: ReactNode;
  }) => (
    <button
      onClick={onClick}
      className={cx(
        'rounded-pill border px-3 py-1 text-xs font-medium transition-colors',
        active ? 'border-ink bg-ink text-paper' : 'border-line text-muted hover:text-ink',
      )}
    >
      {children}
    </button>
  );

  if (error)
    return (
      <div className="rounded-input border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
        {error}
      </div>
    );

  return (
    <div>
      {/* Summary bar — answers "what do I have?" */}
      <div className="mb-5 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-ink">{counts.total} domains</span>
        <span className="text-muted">·</span>
        <Pill
          active={readiness === 'ready'}
          onClick={() => setReadiness(readiness === 'ready' ? '' : 'ready')}
        >
          {counts.ready} Ready
        </Pill>
        <Pill
          active={readiness === 'warming'}
          onClick={() => setReadiness(readiness === 'warming' ? '' : 'warming')}
        >
          {counts.warming} Warming
        </Pill>
        <Pill
          active={false}
          onClick={() => setReadiness(readiness === 'not_ready' ? '' : 'not_ready')}
        >
          {counts.unavailable} Unavailable
        </Pill>
        {counts.expiring > 0 && (
          <span className="inline-flex items-center gap-1 rounded-pill border border-line px-3 py-1 text-xs text-muted">
            <CalendarClock className="h-3.5 w-3.5" /> {counts.expiring} expiring soon
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <button className="btn-outline" title="Import a customer-owned domain">
            <Plus className="h-4 w-4" /> Import
          </button>
          <button className="btn-primary" title="Buy via Cloudflare Registrar">
            <ShoppingCart className="h-4 w-4" /> Buy domain
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domain or theme"
            className="input w-56 pl-8"
          />
        </div>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="input w-40 cursor-pointer"
        >
          <option value="">All sources</option>
          <option value="hook_shared">Shared Pool</option>
          <option value="imported">Imported</option>
          <option value="cloudflare">Cloudflare</option>
        </select>
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          className="input w-40 cursor-pointer"
        >
          <option value="">All styles</option>
          <option value="generic">Generic</option>
          <option value="brand_lookalike">Brand lookalike</option>
        </select>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          className="input w-48 cursor-pointer"
        >
          <option value="">All themes</option>
          {themes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {selectedOrg && (
          <label className="inline-flex items-center gap-2 rounded-input border border-line px-3 py-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={availableOnly}
              onChange={(e) => setAvailableOnly(e.target.checked)}
            />
            Available for {selectedOrg.name}
          </label>
        )}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs font-medium uppercase tracking-wide text-muted">
            <tr>
              <th className="px-5 py-3">Domain</th>
              <th className="px-5 py-3">Readiness</th>
              <th className="px-5 py-3">Condition</th>
              <th className="px-5 py-3 text-right">Last used</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filtered.map((d) => (
              <tr
                key={d.id}
                onClick={() => setSelected(d)}
                className="cursor-pointer transition-colors hover:bg-ink/[0.02]"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Globe2 className="h-4 w-4 shrink-0 text-muted" />
                    <div>
                      <div className="font-medium text-ink">{d.domain}</div>
                      <div className="text-xs text-muted">
                        {SOURCE_LABEL[d.source] ?? d.source}
                        {d.style ? ` · ${d.style === 'brand_lookalike' ? 'Brand' : 'Generic'}` : ''}
                        {d.theme ? ` · ${d.theme}` : ''}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="space-y-1.5">
                    <ReadinessBadge state={d.readiness.state} />
                    <MiniChecks checks={d.readiness.checks} />
                  </div>
                </td>
                <td className="px-5 py-3">
                  <ConditionBadge state={d.condition.state} />
                </td>
                <td className="px-5 py-3 text-right text-xs tabular-nums text-muted">
                  {d.days_since_use !== null ? `${d.days_since_use}d ago` : 'never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <EmptyState
            icon={Globe2}
            title="No domains match"
            hint="Adjust filters, or import/buy a domain to add capacity."
          />
        )}
      </Card>

      {selected && <Drawer d={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
